import { Link } from "react-router-dom";
import { Clock, MessageCircle, Layers, Sparkles, Zap, ArrowRight, GitBranch, Scale, Shield, Baby, HeartPulse, Smartphone, FileText, Users, Globe, QrCode, Search } from "lucide-react";
import { FluxMark, AtomicDecor, Starburst, TimelineRail, MASCOT_URL } from "../components/flux/Decor";

const display = "font-display";

function Navbar() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f1729]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FluxMark className="w-8 h-8" />
          <span className={`${display} font-bold text-lg tracking-[0.02em] text-white`}>
            FLU<span className="text-[#f59e0b]">X</span> <span className="font-light text-gray-300 tracking-wider">CAPACITOR</span>
          </span>
        </div>
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className={`text-gray-300 hover:text-[#f59e0b] transition-colors duration-200 ${display} tracking-wide uppercase text-[11px]`}>Features</a>
          <a href="#how-it-works" className={`text-gray-300 hover:text-[#f59e0b] transition-colors duration-200 ${display} tracking-wide uppercase text-[11px]`}>Process</a>
          <a href="#parallel" className={`text-gray-300 hover:text-[#f59e0b] transition-colors duration-200 ${display} tracking-wide uppercase text-[11px]`}>Parallel Timelines</a>
          <Link
            to="/matrix"
            data-testid="nav-launch-app-btn"
            className={`bg-[#f59e0b] text-[#0f1729] hover:bg-[#fbbf24] font-bold ${display} tracking-wide uppercase text-[11px] rounded-lg px-4 py-2 transition-colors duration-200`}
          >
            Launch The Matrix
          </Link>
        </div>
        <Link
          to="/matrix"
          data-testid="nav-launch-app-btn-mobile"
          className={`md:hidden bg-[#f59e0b] text-[#0f1729] font-bold ${display} tracking-wide uppercase text-[11px] rounded-lg px-4 py-2`}
        >
          Launch
        </Link>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16">
      <div className="absolute inset-0 bg-[#0f1729]">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(245,158,11,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.3) 1px, transparent 1px)`,
            backgroundSize: "80px 80px",
          }}
        />
        <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px]">
          <AtomicDecor className="w-full h-full animate-orbit" />
        </div>
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#f59e0b]/[0.03] rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center py-16">
        <div className="lg:col-span-7 space-y-8">
          <div className="animate-fade-up">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#f59e0b]/[0.08] border border-[#f59e0b]/20 text-[#f59e0b] text-xs ${display} font-medium tracking-[0.15em] uppercase`}>
              <Sparkles className="w-3.5 h-3.5" />
              AI Chronological Intelligence
            </span>
          </div>

          <h1 className={`${display} text-4xl sm:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-[-0.02em] text-white animate-fade-up animation-delay-100`}>
            Your history,<br />
            organized by<br />
            <span className="text-[#f59e0b] relative">
              a genius.
              <svg className="absolute -bottom-2 left-0 w-full h-3" viewBox="0 0 200 12" fill="none">
                <path d="M0 8 Q50 2 100 6 T200 4" stroke="#f59e0b" strokeWidth="2" opacity="0.5" />
              </svg>
            </span>
          </h1>

          <p className="text-base md:text-lg text-gray-300 leading-relaxed max-w-md animate-fade-up animation-delay-200">
            Professor Flux is your AI companion who helps you input, interpret, and beautifully present chronological data — from decade-long court cases to precious family milestones.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 animate-fade-up animation-delay-300">
            <Link
              to="/matrix"
              data-testid="hero-cta-btn"
              className={`inline-flex items-center justify-center bg-[#f59e0b] text-[#0f1729] hover:bg-[#fbbf24] font-bold ${display} text-base px-8 py-4 rounded-xl shadow-[0_0_30px_rgba(245,158,11,0.3)] hover:shadow-[0_0_50px_rgba(245,158,11,0.5)] transition-shadow duration-300 active:scale-[0.97] tracking-wide`}
            >
              Let's bend some time
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="hero-secondary-btn"
              className={`inline-flex items-center justify-center border border-white/15 text-white hover:bg-white/5 hover:border-[#f59e0b]/30 ${display} font-medium text-base px-8 py-4 rounded-xl transition-colors duration-200 active:scale-[0.97]`}
            >
              See how it works
            </a>
          </div>

          <div className="animate-fade-up animation-delay-400 pt-4">
            <TimelineRail />
          </div>
        </div>

        <div className="lg:col-span-5 flex justify-center lg:justify-end animate-fade-up animation-delay-400">
          <div className="relative">
            <div className="absolute -inset-6 rounded-3xl border border-[#f59e0b]/10 rotate-3" />
            <div className="absolute -inset-10 rounded-3xl border border-[#2dd4bf]/5 -rotate-2" />
            <Starburst className="absolute -top-8 -left-8 w-12 h-12" />
            <Starburst className="absolute -bottom-6 -right-6 w-8 h-8" />
            <img
              src={MASCOT_URL}
              alt="Professor Flux — Your AI Time Navigator"
              data-testid="hero-mascot-img"
              className="relative w-72 h-72 lg:w-[22rem] lg:h-[22rem] object-contain animate-float"
            />
            <div className={`absolute -top-4 -right-8 bg-[#fef3c7] text-[#0f1729] rounded-2xl rounded-bl-sm px-4 py-3 shadow-xl max-w-[180px] transform rotate-2`}>
              <p className={`text-xs font-bold ${display} leading-snug`}>Toss me your data — I'll sort out the timeline!</p>
              <div className="absolute bottom-0 left-4 w-3 h-3 bg-[#fef3c7] transform rotate-45 translate-y-1.5" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function FeaturesSection() {
  const features = [
    { icon: <MessageCircle className="w-6 h-6" />, title: "Conversational Input", description: "Just talk to Flux. He asks the right questions, pulls out the details, and logs entries without tedious forms.", color: "#f59e0b" },
    { icon: <Clock className="w-6 h-6" />, title: "Automatic Organization", description: "Flux interprets dates, relationships, cause and effect — structuring everything chronologically.", color: "#2dd4bf" },
    { icon: <Smartphone className="w-6 h-6" />, title: "SMS Evidence Stash", description: "Text a note or photo to your private Flux number and it lands on your timeline instantly — no app, no login, no trace.", color: "#fb7185" },
    { icon: <GitBranch className="w-6 h-6" />, title: "Parallel Timelines", description: "Track multiple threads within the same case — support, safety, procedural — isolate one or overlay them all.", color: "#f59e0b" },
    { icon: <FileText className="w-6 h-6" />, title: "Court-Ready Summaries", description: "One click drafts a numbered, plain-text Chronological Statement of Facts — ready to paste into pleadings and declarations.", color: "#2dd4bf" },
    { icon: <Users className="w-6 h-6" />, title: "Team Collaboration", description: "Invite your lawyer, advocate, or family by email — as viewers or editors — and build the record together.", color: "#fb7185" },
    { icon: <Globe className="w-6 h-6" />, title: "Public Tip Lines", description: "Make a timeline public with its own QR code. Anyone can submit tips; you approve what appears. Built for missing-persons cases.", color: "#f59e0b" },
    { icon: <Zap className="w-6 h-6" />, title: "Pattern Recognition", description: "Feed in years of documents and Flux identifies patterns, connections, and narrative threads.", color: "#2dd4bf" },
  ];

  return (
    <section id="features" className="relative py-32 overflow-hidden">
      <div className="absolute top-12 right-12 opacity-20">
        <AtomicDecor className="w-48 h-48" />
      </div>
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-20">
          <div className="flex items-center gap-4 mb-6">
            <FluxMark className="w-6 h-6" />
            <span className={`${display} text-[11px] tracking-[0.2em] uppercase text-[#f59e0b] font-medium`}>Core Capabilities</span>
            <div className="h-px flex-1 bg-[#f59e0b]/20" />
          </div>
          <h2 className={`${display} text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-[-0.01em]`}>
            Not just a timeline.<br />
            <span className="text-[#f59e0b]">A time machine for your story.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-12">
          {features.map((feature) => (
            <div key={feature.title} className="group flex gap-5 items-start">
              <div
                className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border transition-transform duration-300 group-hover:scale-110"
                style={{ borderColor: `${feature.color}33`, backgroundColor: `${feature.color}0d` }}
              >
                <div style={{ color: feature.color }}>{feature.icon}</div>
              </div>
              <div>
                <h3 className={`${display} text-lg font-semibold text-white mb-2 tracking-tight`}>{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed text-[15px]">{feature.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MockChatPanel() {
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#111c33] p-6 space-y-4">
      <div className="flex items-center gap-2 pb-3 border-b border-white/5">
        <img src={MASCOT_URL} alt="Flux avatar" className="w-8 h-8 object-contain" />
        <span className={`${display} text-xs font-bold text-white tracking-wide`}>PROFESSOR FLUX</span>
        <span className="text-[9px] text-[#2dd4bf] bg-[#2dd4bf]/10 border border-[#2dd4bf]/20 px-2 py-0.5 rounded-full uppercase font-bold tracking-wider">Online</span>
      </div>
      <div className="bg-white/[0.04] rounded-xl rounded-tl-sm px-4 py-3 max-w-[85%]">
        <p className="text-xs text-gray-300 leading-relaxed">Great, I found 14 dated events in those court records! Quick question — was the February hearing continued or vacated? That changes the procedural thread.</p>
      </div>
      <div className="bg-[#f59e0b]/10 border border-[#f59e0b]/15 rounded-xl rounded-tr-sm px-4 py-3 max-w-[80%] ml-auto">
        <p className="text-xs text-gray-200 leading-relaxed">Continued to April 12th — the judge issued a new scheduling order.</p>
      </div>
      <div className="bg-white/[0.04] rounded-xl rounded-tl-sm px-4 py-3 max-w-[85%]">
        <p className="text-xs text-gray-300 leading-relaxed">Perfect. Updating the procedural track now — your timeline is looking beautifully organized. ✓</p>
      </div>
      <div className="flex items-center gap-2 pt-2">
        <div className="w-2 h-2 rounded-full bg-[#f59e0b] animate-pulse-glow" />
        <span className={`${display} text-[10px] tracking-[0.15em] uppercase text-[#f59e0b]/60`}>Flux Active</span>
      </div>
    </div>
  );
}

function HowItWorksSection() {
  const steps = [
    { number: "01", title: "Dump your data", description: "Toss in pleadings, photos, notes, certificates, text messages — anything with a date. Flux handles the mess." },
    { number: "02", title: "Flux talks to you", description: "He asks clarifying questions, identifies what matters, and helps you fill in gaps you didn't know existed." },
    { number: "03", title: "Threads emerge", description: "Flux separates parallel timelines — each aspect of your case or story becomes its own navigable universe." },
    { number: "04", title: "Design your output", description: "Tell Flux who it's for and what you need. He designs a custom presentation in the format that serves you best." },
  ];

  return (
    <section id="how-it-works" className="relative py-32">
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#f59e0b]/20 to-transparent" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-6">
          <FluxMark className="w-6 h-6" />
          <span className={`${display} text-[11px] tracking-[0.2em] uppercase text-[#2dd4bf] font-medium`}>The Process</span>
          <div className="h-px flex-1 bg-[#2dd4bf]/20" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <h2 className={`${display} text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-[-0.01em] mb-14`}>
              From chaos<br />to clarity.
            </h2>
            <div className="space-y-10 relative">
              <div className="absolute left-[18px] top-4 bottom-4 w-px bg-gradient-to-b from-[#f59e0b]/30 via-[#2dd4bf]/20 to-transparent" />
              {steps.map((step) => (
                <div key={step.number} className="flex gap-6 group relative">
                  <div className="flex-shrink-0 relative z-10">
                    <div className="w-9 h-9 rounded-full border-2 border-[#f59e0b]/40 bg-[#0f1729] flex items-center justify-center group-hover:border-[#f59e0b] group-hover:shadow-[0_0_12px_rgba(245,158,11,0.3)] transition-colors duration-300">
                      <span className={`${display} text-[10px] font-bold text-[#f59e0b]`}>{step.number}</span>
                    </div>
                  </div>
                  <div className="pt-1">
                    <h3 className={`${display} text-xl font-semibold text-white mb-2 tracking-tight`}>{step.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-[15px]">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 relative">
            <div className="absolute -inset-4 rounded-2xl border border-[#f59e0b]/10 -rotate-1" />
            <Starburst className="absolute -top-6 -right-6 w-10 h-10" />
            <MockChatPanel />
          </div>
        </div>
      </div>
    </section>
  );
}

function MockTimelinePanel() {
  const events = [
    { color: "#fb7185", date: "JAN 2024", title: "TRO Filed", track: "SAFETY" },
    { color: "#f59e0b", date: "FEB 2024", title: "FL-150 Disclosure Served", track: "SUPPORT" },
    { color: "#2dd4bf", date: "JUN 2025", title: "First Clear Words", track: "FAMILY" },
    { color: "#a78bfa", date: "APR 2026", title: "Settlement Conference Set", track: "PROCEDURAL" },
  ];
  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#111c33] p-6">
      <div className={`${display} text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-5`}>Parallel Track Core Matrix</div>
      <div className="border-l-2 border-white/5 pl-5 ml-1 space-y-4">
        {events.map((ev, i) => (
          <div key={i} className="relative bg-white/[0.03] border border-white/5 rounded-xl p-3.5">
            <div className="absolute -left-[27px] top-4 w-2.5 h-2.5 rounded-full border-2 border-[#111c33]" style={{ backgroundColor: ev.color }} />
            <div className="flex justify-between items-center mb-1.5">
              <span className={`${display} text-[9px] font-bold text-[#f59e0b] bg-[#f59e0b]/5 border border-[#f59e0b]/20 px-2 py-0.5 rounded`}>{ev.date}</span>
              <span className="text-[8px] bg-white/5 px-1.5 py-0.5 rounded text-gray-400 uppercase font-bold tracking-wider">{ev.track}</span>
            </div>
            <p className={`${display} text-xs font-semibold text-white`}>{ev.title}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ParallelTimelinesSection() {
  const threads = [
    { label: "Domestic Violence", color: "#fb7185", years: "2018 — 2026" },
    { label: "Child Custody", color: "#f59e0b", years: "2019 — 2026" },
    { label: "Property Division", color: "#2dd4bf", years: "2020 — 2024" },
    { label: "Support & Finances", color: "#fbbf24", years: "2018 — 2026" },
  ];

  return (
    <section id="parallel" className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-0 w-full h-full opacity-[0.02]"
          style={{ backgroundImage: `repeating-linear-gradient(-45deg, transparent, transparent 80px, rgba(245,158,11,0.5) 80px, rgba(245,158,11,0.5) 81px)` }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="flex items-center gap-4 mb-6">
          <FluxMark className="w-6 h-6" />
          <span className={`${display} text-[11px] tracking-[0.2em] uppercase text-[#fb7185] font-medium`}>Parallel Universes</span>
          <div className="h-px flex-1 bg-[#fb7185]/20" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6">
            <h2 className={`${display} text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-[-0.01em] mb-6`}>
              One case.<br />
              <span className="text-[#f59e0b]">Multiple dimensions.</span>
            </h2>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-10 max-w-lg">
              An 8-year court case isn't one story — it's five running simultaneously. Flux separates each thread into its own navigable timeline while preserving the connections between them.
            </p>

            <div className="space-y-3 p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
              {threads.map((thread, i) => (
                <div key={thread.label} className="flex items-center gap-4">
                  <div className="w-3 h-3 rounded-sm flex-shrink-0 rotate-45" style={{ backgroundColor: thread.color }} />
                  <span className={`text-sm ${display} font-medium text-gray-300 w-40`}>{thread.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-white/[0.04] overflow-hidden relative">
                    <div className="absolute inset-y-0 left-0 rounded-full" style={{ backgroundColor: thread.color, opacity: 0.6, width: `${70 + i * 8}%` }} />
                  </div>
                  <span className={`text-[11px] text-gray-400 ${display} tracking-wide whitespace-nowrap hidden sm:block`}>{thread.years}</span>
                </div>
              ))}
            </div>

            <p className={`text-sm text-gray-400 mt-6 ${display}`}>
              View threads in isolation or layered together to see how events in one dimension triggered changes across all others.
            </p>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="absolute -inset-4 rounded-2xl border border-[#2dd4bf]/[0.08] rotate-1" />
            <Starburst className="absolute -top-6 -left-6 w-10 h-10" />
            <MockTimelinePanel />
          </div>
        </div>
      </div>
    </section>
  );
}

function StashSpotlightSection() {
  return (
    <section className="relative py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-6">
          <FluxMark className="w-6 h-6" />
          <span className={`${display} text-[11px] tracking-[0.2em] uppercase text-[#fb7185] font-medium`}>New Capabilities</span>
          <div className="h-px flex-1 bg-[#fb7185]/20" />
        </div>
        <h2 className={`${display} text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-[-0.01em] mb-16`}>
          Evidence in. Answers out.<br />
          <span className="text-[#fb7185]">From anywhere.</span>
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] space-y-5" data-testid="landing-sms-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#2dd4bf]/30 bg-[#2dd4bf]/5">
                <Smartphone className="w-6 h-6 text-[#2dd4bf]" />
              </div>
              <h3 className={`${display} text-xl font-semibold text-white tracking-tight`}>SMS Evidence Stash</h3>
            </div>
            <p className="text-gray-300 leading-relaxed text-[15px]">
              Link your phone once. Then text notes or photos to your Flux number and they're filed on your timeline in seconds — dated, tagged, and safe. Nothing to install, nothing left on your phone but an ordinary text you can delete.
            </p>
            <div className="bg-[#0b1222] border border-white/5 rounded-xl p-4 space-y-3">
              <div className="bg-[#2dd4bf]/10 border border-[#2dd4bf]/15 rounded-xl rounded-tr-sm px-4 py-2.5 max-w-[80%] ml-auto">
                <p className="text-xs text-gray-200">He showed up at my work again today around 3pm 📷</p>
              </div>
              <div className="bg-white/[0.04] rounded-xl rounded-tl-sm px-4 py-2.5 max-w-[85%]">
                <p className="text-xs text-gray-300">⚡ Stashed! Professor Flux is filing your note and 1 photo onto your timeline now.</p>
              </div>
            </div>
          </div>

          <div className="p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] space-y-5" data-testid="landing-tipline-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-[#f59e0b]/30 bg-[#f59e0b]/5">
                <QrCode className="w-6 h-6 text-[#f59e0b]" />
              </div>
              <h3 className={`${display} text-xl font-semibold text-white tracking-tight`}>Public Tip Lines</h3>
            </div>
            <p className="text-gray-300 leading-relaxed text-[15px]">
              Take a timeline public for a missing-persons case or community investigation. Print the QR code on flyers — anyone who scans it sees the verified chronology and can submit a tip. You're the admin: every tip waits in your moderation queue until you approve it.
            </p>
            <div className="bg-[#0b1222] border border-white/5 rounded-xl p-4 flex items-center gap-4">
              <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center shrink-0">
                <QrCode className="w-12 h-12 text-[#0f1729]" />
              </div>
              <div className="space-y-1">
                <p className={`${display} text-xs font-bold text-white uppercase tracking-wide`}>Scan to submit a tip</p>
                <p className="text-[11px] text-gray-400">"Saw her at the Main St gas station around 8pm" → <span className="text-[#f59e0b] font-mono">PENDING REVIEW</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function UseCasesSection() {
  const cases = [
    { title: "Complex Litigation", description: "Organize years of pleadings, discovery, and court orders into parallel timelines a judge can follow in minutes.", icon: <Scale className="w-7 h-7 text-[#f59e0b]" /> },
    { title: "Personal Protection", description: "Document patterns of behavior over time — even by discreet text message. Build an undeniable chronological record that speaks for itself.", icon: <Shield className="w-7 h-7 text-[#fb7185]" /> },
    { title: "Missing Persons Investigations", description: "Publish a public timeline with a scannable QR tip line. The community submits sightings; you verify and publish them as the case unfolds.", icon: <Search className="w-7 h-7 text-[#a78bfa]" /> },
    { title: "Family Milestones", description: "Every first word, first step, and precious moment — organized beautifully and shareable with family.", icon: <Baby className="w-7 h-7 text-[#2dd4bf]" /> },
    { title: "Medical History", description: "Track symptoms, treatments, and outcomes across providers and years. See the full picture your doctors can't.", icon: <HeartPulse className="w-7 h-7 text-[#fbbf24]" /> },
    { title: "Team Casework", description: "Attorneys, advocates, and families working one case — shared timelines with viewer and editor roles keep everyone on the same page.", icon: <Users className="w-7 h-7 text-[#f59e0b]" /> },
  ];

  return (
    <section className="relative py-32">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-6">
          <FluxMark className="w-6 h-6" />
          <span className={`${display} text-[11px] tracking-[0.2em] uppercase text-[#2dd4bf] font-medium`}>Use Cases</span>
          <div className="h-px flex-1 bg-[#2dd4bf]/20" />
        </div>

        <h2 className={`${display} text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-[-0.01em] mb-16`}>
          Built for anyone who needs<br />
          <span className="text-[#2dd4bf]">the truth, organized.</span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cases.map((useCase, i) => (
            <div
              key={useCase.title}
              className={`p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-[#f59e0b]/20 hover:bg-white/[0.04] transition-colors duration-300 ${i % 2 === 1 ? "md:translate-y-6" : ""}`}
            >
              <div className="flex items-start gap-5">
                {useCase.icon}
                <div>
                  <h3 className={`${display} text-xl font-semibold text-white mb-2 tracking-tight`}>{useCase.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-[15px]">{useCase.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative py-32 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
          <AtomicDecor className="w-[500px] h-[500px] opacity-30 animate-orbit" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 text-center relative">
        <FluxMark className="w-16 h-16 mx-auto mb-8" />
        <h2 className={`${display} text-3xl md:text-5xl font-bold text-white leading-[1.05] tracking-[-0.01em] mb-6`}>
          Ready to navigate<br />your timeline?
        </h2>
        <p className="text-gray-300 text-base md:text-lg mb-10 max-w-xl mx-auto">
          The capacitor is fully charged. Step into the matrix and let Professor Flux organize your story with AI.
        </p>

        <Link
          to="/matrix"
          data-testid="cta-launch-btn"
          className={`inline-flex items-center bg-[#f59e0b] text-[#0f1729] hover:bg-[#fbbf24] font-bold ${display} text-lg px-10 py-5 rounded-xl shadow-[0_0_40px_rgba(245,158,11,0.35)] hover:shadow-[0_0_60px_rgba(245,158,11,0.5)] transition-shadow duration-300 active:scale-[0.97] tracking-wide`}
        >
          Enter The Matrix
          <Sparkles className="w-5 h-5 ml-2" />
        </Link>

        <div className="mt-16">
          <TimelineRail />
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-white/5 py-12">
      <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <FluxMark className="w-6 h-6 opacity-60" />
          <span className={`${display} text-sm text-gray-400 tracking-wide`}>Flux Capacitor © 2026</span>
        </div>
        <p className={`text-xs text-gray-500 ${display} tracking-wide`}>
          AI-powered chronological storytelling. Built for those who need the truth, organized.
        </p>
        <div className="flex items-center gap-5 text-xs text-gray-400">
          <Link to="/faq" data-testid="footer-faq-link" className={`hover:text-[#f59e0b] transition-colors duration-200 ${display}`}>FAQ</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className={`hover:text-[#f59e0b] transition-colors duration-200 ${display}`}>Privacy Policy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className={`hover:text-[#f59e0b] transition-colors duration-200 ${display}`}>Terms of Service</Link>
          <Link to="/cookies" data-testid="footer-cookies-link" className={`hover:text-[#f59e0b] transition-colors duration-200 ${display}`}>Cookie Policy</Link>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0f1729]" data-testid="landing-page">
      <Navbar />
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <ParallelTimelinesSection />
      <StashSpotlightSection />
      <UseCasesSection />
      <CTASection />
      <Footer />
    </div>
  );
}
